import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';

import { useAuth } from './useAuth';
import { getEnv } from 'shared/get-env';

interface WebSocketContextType {
  isOnline: boolean;
  socket: Socket | null;
  attempts: number;
}

const WebSocketContext = createContext<WebSocketContextType>({
  isOnline: false,
  socket: null,
  attempts: 0,
});

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { auth } = useAuth();
  const { currentUser } = useAuth();

  const [attempts, setAttempts] = useState(0);
  const [isOnline, setIsOnline] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);

  const maxAttempts = 10;
  const url = getEnv('REACT_APP_API_URL');

  useEffect(() => {
    if (!auth?.activeProfile) return;
    if (!url) return;

    let currentSocket: Socket | null = null;
    let isUnmounted = false;
    let currentAttempts = 1;

    const connect = () => {
      if (currentAttempts > maxAttempts) return;
      setAttempts(currentAttempts);

      currentSocket = io(url, { autoConnect: true, reconnection: false });
      setSocket(currentSocket);

      currentSocket.emit('authenticate', {
        token: currentUser?.access_token,
      });

      currentSocket.on('authenticated', () => {
        // Autenticado com sucesso
        setIsOnline(true);
      });

      currentSocket.on('unauthorized', () => {
        // Falha na autenticação
        setIsOnline(false);
        currentSocket.disconnect();
      });

      currentSocket.on('disconnect', () => {
        if (isUnmounted) return;
        setIsOnline(false);
        if (currentAttempts < maxAttempts) {
          setTimeout(() => {
            currentAttempts += 1;
            connect();
          }, 2000);
        } else {
          console.log(
            'Número máximo de tentativas atingido. Não será feita nova tentativa.',
          );
        }
      });

      currentSocket.on('connect_error', (error) => {
        if (isUnmounted) return;
        setIsOnline(false);
        currentSocket?.disconnect();
      });
    };

    connect();

    return () => {
      isUnmounted = true;
      currentSocket?.disconnect();
    };
  }, [url, auth]);

  return (
    <WebSocketContext.Provider value={{ isOnline, socket, attempts }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => useContext(WebSocketContext);
